import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../hooks';
import { logout } from '../../features/user/user-slice';
import { useNavigate } from 'react-router-dom';
import { 
  INACTIVITY_CONFIG, 
  InactivityTimerConfig, 
  UseInactivityTimerReturn 
} from './inactivity-timer-types';

export const useInactivityTimer = (
  isEnabled: boolean = true,
  config: InactivityTimerConfig = {}
): UseInactivityTimerReturn => {
  const {
    inactivityTimeout = INACTIVITY_CONFIG.INACTIVITY_TIMEOUT,
    warningTimeout = INACTIVITY_CONFIG.WARNING_TIMEOUT,
    countdownInterval = INACTIVITY_CONFIG.COUNTDOWN_INTERVAL,
    activityEvents = INACTIVITY_CONFIG.ACTIVITY_EVENTS,
  } = config;

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(warningTimeout / 1000);
  const [isActive, setIsActive] = useState(isEnabled);

  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const showWarningModalRef = useRef<boolean>(false);
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Reset all timers
  const resetTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  // Handle logout
  const performLogout = useCallback(() => {
    showWarningModalRef.current = false;
    resetTimers();
    setShowWarningModal(false);
    setIsActive(false);
    dispatch(logout());
    navigate('/');
  }, [dispatch, navigate, resetTimers]);

  // Start countdown timer for warning modal
  const startCountdownTimer = useCallback(() => {
    // Clear any existing countdown timer first
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    const initialTime = Math.floor(warningTimeout / 1000);
    setRemainingTime(initialTime);

    countdownTimerRef.current = setInterval(() => {
      setRemainingTime((prevTime) => {
        const newTime = prevTime - 1;
        
        if (newTime <= 0) {
          performLogout();
          return 0;
        }
        
        return newTime;
      });
    }, countdownInterval);
  }, [warningTimeout, countdownInterval, performLogout]);

  // Show warning modal and start countdown
  const showWarning = useCallback(() => {
    showWarningModalRef.current = true;
    setShowWarningModal(true);
    startCountdownTimer();
  }, [startCountdownTimer]);

  // Start inactivity timer
  const startInactivityTimer = useCallback(() => {
    if (!isEnabled) return;

    resetTimers();
    setIsActive(true);
    inactivityTimerRef.current = setTimeout(showWarning, inactivityTimeout);
  }, [isEnabled, inactivityTimeout, resetTimers, showWarning]);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    if (!isEnabled || !isActive) return;
    
    // If warning modal is showing, don't reset - let user explicitly choose
    if (!showWarningModalRef.current) {
      startInactivityTimer();
    }
  }, [isEnabled, isActive, startInactivityTimer]);

  // User chooses to stay logged in
  const stayLoggedIn = useCallback(() => {
    showWarningModalRef.current = false;
    setShowWarningModal(false);
    resetTimers();
    startInactivityTimer();
  }, [resetTimers, startInactivityTimer]);

  // User chooses to logout
  const logoutNow = useCallback(() => {
    performLogout();
  }, [performLogout]);

  // Reset timer manually (for external use)
  const resetTimer = useCallback(() => {
    if (isEnabled && isActive && !showWarningModalRef.current) {
      startInactivityTimer();
    }
  }, [isEnabled, isActive, startInactivityTimer]);

  // Set up activity listeners and mobile-specific handling
  useEffect(() => {
    if (!isEnabled) {
      setIsActive(false);
      return;
    }

    setIsActive(true);

    const eventHandler = (event: Event) => {
      // Only reset timer if not showing warning modal
      if (!showWarningModalRef.current && isEnabled && isActive) {
        resetTimers();
        inactivityTimerRef.current = setTimeout(showWarning, inactivityTimeout);
      }
    };

    // Handle app visibility changes (mobile background/foreground)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isEnabled) {
        // Only restart if not showing warning modal
        if (!showWarningModalRef.current) {
          resetTimers();
          inactivityTimerRef.current = setTimeout(showWarning, inactivityTimeout);
        }
      }
    };

    // Handle page focus/blur (mobile/tablet app switching)
    const handleFocus = () => {
      if (isEnabled && !showWarningModalRef.current) {
        resetTimers();
        inactivityTimerRef.current = setTimeout(showWarning, inactivityTimeout);
      }
    };

    // Add event listeners for all activity events
    activityEvents.forEach((event) => {
      document.addEventListener(event, eventHandler, true);
    });

    // Add mobile-specific listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Start initial timer
    if (!showWarningModalRef.current) {
      startInactivityTimer();
    }

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, eventHandler, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      // Only reset timers if modal is not showing to preserve countdown
      if (!showWarningModalRef.current) {
        resetTimers();
      }
    };
  }, [isEnabled, isActive, inactivityTimeout, activityEvents, showWarning, resetTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetTimers();
    };
  }, [resetTimers]);

  // Update active state when enabled changes
  useEffect(() => {
    setIsActive(isEnabled);
    if (!isEnabled) {
      showWarningModalRef.current = false;
      resetTimers();
      setShowWarningModal(false);
    }
  }, [isEnabled, resetTimers]);

  // Sync ref with state
  useEffect(() => {
    showWarningModalRef.current = showWarningModal;
  }, [showWarningModal]);

  return {
    showWarningModal,
    remainingTime,
    isActive,
    stayLoggedIn,
    logoutNow,
    resetTimer,
  };
};